import * as React from 'react';
import { observer } from 'mobx-react-lite';
import CalendarAgendaPane, { CalendarAgendaPaneHandle } from './CalendarAgendaPane';
import { CalendarSurface } from 'owa-calendar-surface-core';
import { DateRange } from 'owa-datetime-utils';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import getCalendarItems from '../actions/getCalendarItems';
import publishedCalendarConfig from '../utils/getPublishedCalendarConfig';
import {
    getDataFetchRange,
    getPublishedFolderPath,
    updateDataFetchRange,
} from 'owa-publishedcalendar-settings';
import { getSurfaceStore } from '../store/publishedCalendarSurfaceStore';
import ModuleRenderer from './ModuleRenderer';
import PublishedCalendarCommandBar from './PublishedCalendarCommandBar';
import { PublishedCalendarLeftPane } from 'owa-publishedcalendar-leftpane';
import PublishedCalendarNameBar from './PublishedCalendarNameBar';
import { updateDateRange, updateDateRangeType } from 'owa-calendar-surface';
import { registerFocusElements } from '../utils/registerFocusElements';

const styles = require('./PublishedCalendar.scss');

/**
 * Published calendar UI
 */
export default observer(function PublishedCalendar() {
    React.useEffect(() => {
        if (getPublishedFolderPath()) {
            getCalendarItems();
        }
        registerFocusElements(getCalendarAgendaPaneRef);
    }, [getDataFetchRange()]);

    const calendarAgendaPaneRef = React.useRef<CalendarAgendaPaneHandle>();
    const getCalendarAgendaPaneRef = React.useCallback(() => {
        return calendarAgendaPaneRef.current;
    }, []);

    return (
        <div className={styles.mainContent}>
            <div className={styles.leftPane}>
                <PublishedCalendarLeftPane
                    surfaceStore={getSurfaceStore()}
                    onDateRangeChange={handleDateRangeChange}
                />
            </div>
            <div className={styles.rightPane}>
                <PublishedCalendarNameBar />
                <PublishedCalendarCommandBar
                    store={getSurfaceStore()}
                    onDateRangeChange={handleDateRangeChange}
                    onDateRangeTypeChange={handleDateRangeTypeChange}
                />
                <div className={styles.surfaceAgenda}>
                    <CalendarSurface config={publishedCalendarConfig} store={getSurfaceStore()} />
                    <CalendarAgendaPane
                        date={getSurfaceStore().selectedDate}
                        events={getSurfaceStore().calendarItems}
                        ref={calendarAgendaPaneRef}
                    />
                </div>
            </div>
            <ModuleRenderer />
        </div>
    );
});

/**
 * Called when date range is changed
 */
function handleDateRangeChange(newDateRange: DateRange) {
    updateDateRange(newDateRange, getSurfaceStore());
    updateDataFetchRange(newDateRange, getSurfaceStore().userOptions.weekStartDay);
}

/**
 * Called when date range type is changed
 */
function handleDateRangeTypeChange(
    newDateRangeType: DateRangeType,
    newDateRange: DateRange,
    newNumOfDaysInDayRange?: number
) {
    updateDateRangeType(newDateRangeType, getSurfaceStore(), newNumOfDaysInDayRange);
    updateDataFetchRange(newDateRange, getSurfaceStore().userOptions.weekStartDay);
}
